import axios from 'axios';
import { authStorage } from './storage';
import { REFRESH_TOKEN_URL, USER_INFO_PATH } from './constants/url';
import { endSessionAndSignIn, isSessionAlive } from './session';

/**
 * 요청에 붙일 수 있는 우리 표시들.
 *
 * <p>`_keepSessionOn401` 은 **401 을 "세션 만료" 가 아니라 "권한 없음" 으로 받겠다**는 뜻이다.
 * 이 레포에는 서버가 403 대신 401 로 답하는 엔드포인트가 있고(사건 메일·첨부 §7.2), 거기서는
 * 401 이 정상 동작으로도 난다 — 그걸 세션 만료로 읽으면 쓰던 화면에서 로그인 폼으로 튕긴다.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    _keepSessionOn401?: boolean;
    _retriedAfterReissue?: boolean;
  }
}

const baseURL = process.env.NEXT_PUBLIC_API_URL;

/** 실패한 요청이 유저 정보 조회 자신인가 — 그렇다면 세션 상태를 다시 물어볼 필요가 없다. */
const isSessionProbe = (url: string | undefined): boolean => !!url && url.includes(USER_INFO_PATH);

/**
 * 재발급 요청에 거는 제한 시간. 미들웨어(TOKEN_REISSUE_TIMEOUT_MS)와 같은 값이다.
 *
 * 이 요청이 매달리면 인터셉터의 await 가 영원히 끝나지 않는다. 그러면 로그인 화면으로 보내는
 * 코드에 도달하지 못하고, 호출부는 성공도 실패도 받지 못해 로딩 화면에 갇힌다.
 * 에러 리포트조차 남지 않아 제일 찾기 어려운 형태로 죽는다.
 */
const TOKEN_REISSUE_TIMEOUT_MS = 5000;

/**
 * 재발급의 세 가지 결말. 「실패」를 둘로 가르는 이유는 아래 인터셉터의 `_keepSessionOn401` 분기다.
 * 분류 기준은 `middleware.ts` 의 `requestTokenReissue` 와 **같아야 한다** — 갈리면 서버 판정과 클라이언트 판정 사이에
 * 사용자가 낀다(한쪽은 쿠키를 지우고 다른 쪽은 로그인 상태로 그린다).
 * - `ok`: 새 토큰을 심었다.
 * - `rejected`: 서버가 리프레시 토큰을 **거절**했다(HTTP 401·403, 또는 HTTP 200 봉투의 `status` 가 401·403), 혹은 리프레시
 *   토큰이 아예 없다. 세션이 죽었다는 확답이다.
 * - `unavailable`: 거절이 아닌 모든 실패 — 네트워크 오류·5초 타임아웃·5xx·404(라우팅 누락)·429·408·JSON 아닌 200·봉투가
 *   토큰도 인증 실패도 아닌 200. 세션이 죽었는지는 알 수 없다.
 */
type ReissueOutcome = { kind: 'ok'; accessToken: string } | { kind: 'rejected' } | { kind: 'unavailable' };

/** 서버가 토큰 자체를 거절한 상태 코드. 그 외의 실패는 서버 사정으로 본다(미들웨어 `isTokenRejection` 과 같다). */
const isTokenRejection = (status: unknown): boolean => status === 401 || status === 403;

/**
 * 리프레시 토큰으로 새 토큰을 받아 심는다. 던지지 않는다 — 결말은 {@link ReissueOutcome} 으로 돌려준다.
 *
 * 응답 형태를 확인하고 나서 심는다. 예전에는 data.result.accessToken 을 곧장 넘겨서,
 * 서버가 { status, message, result: null } 로 실패를 알려주면 그 자리에서 TypeError 가 났다.
 */
const reissueTokens = async (): Promise<ReissueOutcome> => {
  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) return { kind: 'rejected' };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TOKEN_REISSUE_TIMEOUT_MS);

  // 제한 시간은 **본문을 다 읽을 때까지** 건다. 헤더를 받은 순간 타이머를 풀면 서버가 200 헤더만 보내고 본문을
  // 끝내지 않을 때 `response.json()` 이 영원히 매달린다 — 호출부는 성공도 실패도 못 받고 로딩에 갇힌다(2026-09-21 Codex 3라운드).
  try {
    let response: Response;
    try {
      response = await fetch(REFRESH_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          RefreshToken: refreshToken,
        },
        body: JSON.stringify({
          domain: window.location.hostname,
        }),
        signal: controller.signal,
      });
    } catch {
      // 중단(timeout)·네트워크 오류가 여기로 온다. 서버 답을 못 들은 것이지 거절당한 게 아니다.
      return { kind: 'unavailable' };
    }

    // 401·403 만 「서버가 이 토큰을 거절했다」다. 404·429·5xx 까지 세션 무효로 읽으면 배포 사고·과부하 한 번에 전 사용자가 튕긴다.
    if (isTokenRejection(response.status)) return { kind: 'rejected' };
    if (!response.ok) return { kind: 'unavailable' };

    let payload: { status?: unknown; result?: Record<string, unknown> | null } | null;
    try {
      payload = await response.json();
    } catch {
      // 200 인데 JSON 이 아니다(게이트웨이 에러 페이지 등), 또는 본문을 읽다 제한 시간에 중단됐다. 서버 사정으로 본다.
      return { kind: 'unavailable' };
    }

    const result = payload?.result;
    if (typeof result?.accessToken === 'string' && typeof result?.refreshToken === 'string' && typeof result?.expireDate === 'string') {
      authStorage.setToken(result.accessToken, result.refreshToken, result.expireDate, result.refreshDuration as number);
      return { kind: 'ok', accessToken: result.accessToken };
    }

    // 백엔드는 HTTP 200 봉투로 실패를 내려주는 경로가 있다(`{ status: 401, message, result: null }`). 그 봉투의 401·403 만 거절이다.
    return isTokenRejection(payload?.status) ? { kind: 'rejected' } : { kind: 'unavailable' };
  } finally {
    clearTimeout(timeoutId);
  }
};

export const axiosInstance = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  config => {
    const token = authStorage.getAccessToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// 응답 인터셉터
axiosInstance.interceptors.response.use(
  response => {
    if (response.status !== 200) {
      throw new Error(response.data.code);
    }
    return response;
  },
  async error => {
    const status: unknown = error.response?.status;

    if (status === 401) {
      const originalRequest = error.config;

      // 재시도는 딱 한 번이다.
      //
      // 재발급이 성공했는데도 같은 요청이 다시 401 로 돌아오는 경우가 있다 — 토큰은 멀쩡한데
      // 그 리소스에 대한 권한이 없을 때(서버가 403 대신 401 로 답하는 엔드포인트) 그렇다.
      // 가드가 없으면 재발급 → 재시도 → 401 → 재발급 … 이 끝없이 돌고, 그동안
      // 아래 endSessionAndSignIn() 에는 영영 도달하지 못한다. 화면은 로딩 상태로 멈춘다.
      // 두 번째 401 은 "토큰 문제가 아니다"라는 답이므로 세션을 접고 로그인으로 보낸다.
      const reissue: ReissueOutcome | null = originalRequest?._retriedAfterReissue ? null : await reissueTokens();

      if (reissue?.kind === 'ok') {
        originalRequest._retriedAfterReissue = true;
        originalRequest.headers.Authorization = `Bearer ${reissue.accessToken}`;
        return axiosInstance(originalRequest);
      }

      // **401 이 "권한 없음" 인 경로는 세션을 접지 않는다.**
      // 이 레포에는 서버가 403 대신 401 로 답하는 엔드포인트가 있다(사건 메일·첨부 §7.2, 계약 화면이
      // 자동으로 읽는 연결 접수 등). 거기서는 401 이 정상 동작으로도 난다 — 담당 변호사가 메일 공개를
      // 내리면, 그 목록을 띄워 둔 고객이 「내려받기」를 누르는 순간 401 이다. 그것까지 세션 만료로
      // 읽으면 **고객이 쓰던 화면에서 로그인 폼으로 튕긴다.** 그런 호출은 요청에 표시를 달아 둔다.
      //
      // 표시가 달린 요청이 세션을 지키는 경우는 둘이다.
      // ① 재발급에 성공하고 재시도했는데도 401 — 토큰은 멀쩡하고 권한이 없는 것.
      // ② 재발급 서버에 **닿지 못한** 경우(`unavailable`: 네트워크·타임아웃·5xx) — 세션이 죽었는지 알 수 없는데
      //    선택적인 조회 하나 때문에 정상 화면에서 로그아웃시킬 수는 없다(2026-09-21 Codex 리뷰). 세션이 정말
      //    죽었다면 다음 일반 요청이 곧 다시 401 을 맞고 그때 접힌다.
      // 반대로 재발급이 **거절**된 경우(`rejected`)는 토큰이 진짜 죽은 것이라 표시와 무관하게 접는다 —
      // 그것까지 삼키면 사용자가 **죽은 세션을 쥔 채 화면에 갇힌다**.
      if (originalRequest?._keepSessionOn401 && (originalRequest?._retriedAfterReissue || reissue?.kind === 'unavailable')) {
        return Promise.reject(error);
      }

      // 예전에는 재발급이 '실패했지만 던지지는 않은' 경우(ok=false, 리프레시 토큰 없음)에
      // 아무 처리 없이 빠져나갔다. 그러면 무효 토큰을 쥔 채 화면에 남아 같은 401 을 계속 맞는다.
      endSessionAndSignIn();
      return Promise.reject(error);
    }

    // 낡은 쿠키를 가진 기존 사용자는 401 이 아니라 500 으로 들어온다. 서버가 토큰을 못 읽으면
    // 인증 실패로 응답하는 게 아니라, 신원이 안 채워진 채 컨트롤러까지 가서 터지기 때문이다.
    // 그 500 을 그냥 흘려보내면 사용자는 깨진 화면 앞에서 빠져나갈 방법이 없다.
    //
    // 그렇다고 5xx 자체를 로그아웃 신호로 쓰지는 않는다 — 백엔드가 잠깐 흔들릴 때 전 사용자가 튕긴다.
    // 5xx 는 '확인해볼 이유'일 뿐이고, 판정은 유저 정보 조회 한 곳에서만 내린다.
    // 죽은 세션으로 판명되면 쿠키를 지우고 로그인으로 보낸다 — 다시 로그인하면서 새 쿠키가 낡은 쿠키를 덮어쓴다.
    if (typeof status === 'number' && status >= 500 && authStorage.getAccessToken()) {
      const alive = isSessionProbe(error.config?.url) ? false : await isSessionAlive();
      if (!alive) endSessionAndSignIn();
    }

    return Promise.reject(error);
  },
);
